// Web-only sizing helpers for FTSHtmlJsRenderer (analytic proposal P1: scale-to-fit + auto-height).
//
// Pure functions, kept out of assembleHost on purpose: assembleHost is the parity core shared
// byte-for-byte with the render service, while everything here only concerns the on-screen frame.

// CSS page sizes at 96dpi, portrait (width x height in px). Matches the print frame's A4 box in
// FTSHtmlJsRenderer._printHtml (794 x 1123).
const PAGE_SIZES_PX = {
  a4: [794, 1123],
  a3: [1123, 1587],
  a5: [559, 794],
  letter: [816, 1056],
  legal: [816, 1344],
  tabloid: [1056, 1632],
};

/** "210mm" / "8.5in" / "794px" / "794" -> px (null when unparseable). */
export function lengthToPx(value) {
  if (value == null) return null;
  const m = String(value).trim().match(/^(\d+(?:\.\d+)?)\s*(mm|cm|in|pt|px)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  switch ((m[2] || "px").toLowerCase()) {
    case "mm": return n * 96 / 25.4;
    case "cm": return n * 96 / 2.54;
    case "in": return n * 96;
    case "pt": return n * 96 / 72;
    default: return n;
  }
}

/**
 * The page box width in px the bundle lays out against (what the frame is given when fitting):
 * explicit page.width wins; else page.size (A4 default), swapped for landscape.
 */
export function pageWidthPx(page) {
  page = page || {};
  const explicit = lengthToPx(page.width);
  if (explicit) return Math.round(explicit);
  const key = String(page.size || "A4").trim().toLowerCase();
  const dims = PAGE_SIZES_PX[key] || PAGE_SIZES_PX.a4;
  const landscape = page.orientation === "landscape";
  return landscape ? dims[1] : dims[0];
}

/**
 * Scale factor for a frame laid out at pageWidth inside a container of containerWidth: < 1 only
 * when fitting AND the container is narrower (never upscales). 1 when unknown.
 */
export function scaleFor(containerWidth, pageWidth, fit) {
  if (!fit) return 1;
  const c = Number(containerWidth);
  const p = Number(pageWidth);
  if (!(c > 0) || !(p > 0) || c >= p) return 1;
  return Math.max(0.1, Math.round((c / p) * 1000) / 1000);
}

/**
 * The parent<->frame bridge: a script appended to the PREVIEW document (never the print frame) that
 * posts `ready` (with the runtime's ok/error) once the same barrier the render service awaits has
 * settled, then `size` whenever the content height changes. postMessage works from a sandboxed
 * (opaque-origin) frame to its parent; the parent matches messages on event.source + this token,
 * so a stray frame cannot spoof another renderer. Messages carry no data out of the frame beyond
 * ok / error text / height.
 */
export function bridgeScript(token) {
  const t = JSON.stringify(String(token == null ? "" : token));
  return (
    "<scr" + "ipt>(function(){if(window.parent===window)return;var T=" + t + ";" +
    "function post(type,x){try{var m={source:'inistate-htmljs',token:T,type:type};if(x){for(var k in x)m[k]=x[k];}window.parent.postMessage(m,'*');}catch(e){}}" +
    "function h(){var d=document,b=d.body,e=d.documentElement;return Math.max(b?b.scrollHeight:0,e?e.scrollHeight:0,b?b.offsetHeight:0,e?e.offsetHeight:0);}" +
    "var last=-1;function tick(){var v=h();if(v!==last){last=v;post('size',{height:v});}}" +
    "var g=window.__inistatePagedReady||window.__inistateReady||Promise.resolve(true);" +
    "g.then(function(ok){tick();post('ready',{ok:ok!==false&&!window.__inistateError,error:window.__inistateError||null,height:h()});}," +
    "function(e){tick();post('ready',{ok:false,error:String(window.__inistateError||e||'failed'),height:h()});});" +
    "window.addEventListener('load',tick);setInterval(tick,500);" +
    "try{if(window.ResizeObserver){new ResizeObserver(tick).observe(document.documentElement);}}catch(e){}" +
    "})();</scr" + "ipt>"
  );
}

/** Append the bridge script to a fully built host document (before </body> when present). */
export function injectBridge(html, token) {
  const s = bridgeScript(token);
  return html.indexOf("</body>") > -1 ? html.replace("</body>", () => s + "</body>") : html + s;
}
